import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setupTotp, confirmTotp, clearPending2faToken } from "./authService";
import config from "../../config/config";
import useBootstrapCSS from "../../hooks/useBootstrapCSS";
import codLogo from "./CoD-Logo.png";
import "./login.css";

const LOGIN_URL = config["backend-url"];

const TotpSetup = () => {
    useBootstrapCSS();
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const isMidLogin = !token; // true when called from login flow (require_totp_setup)

    // null = loading (post-login check), false = not set up, true = already active
    const [totpEnabled, setTotpEnabled] = useState(isMidLogin ? false : null);
    const [step, setStep] = useState("start"); // "start" | "scan" | "done"
    const [qrCode, setQrCode] = useState("");
    const [secret, setSecret] = useState("");
    const [code, setCode] = useState("");
    const [message, setMessage] = useState({ text: "", type: "" });
    const [isLoading, setIsLoading] = useState(false);

    // Post-login: fetch user details to check totp_enabled before rendering
    useEffect(() => {
        if (isMidLogin) return;

        const fetchUserDetails = async () => {
            try {
                const response = await fetch(`${LOGIN_URL}/get_user_details`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                setTotpEnabled(data.totp_enabled ?? false);
            } catch {
                setTotpEnabled(false);
            }
        };

        fetchUserDetails();
    }, [isMidLogin, token]);

    const handleSetup = async () => {
        setIsLoading(true);
        setMessage({ text: "", type: "" });
        try {
            const result = await setupTotp(token);
            setQrCode(result.qr_code);
            setSecret(result.secret);
            setStep("scan");
        } catch (error) {
            setMessage({ text: error.message || "Failed to generate QR code.", type: "fail" });
        }
        setIsLoading(false);
    };

    const handleConfirm = async () => {
        const trimmed = code.trim();
        if (!trimmed || trimmed.length !== 6) {
            setMessage({ text: "Please enter the 6-digit code from your authenticator app.", type: "fail" });
            return;
        }
        setIsLoading(true);
        setMessage({ text: "", type: "" });
        try {
            const result = await confirmTotp(token, trimmed);
            // Mid-login: backend issues JWT tokens after TOTP setup
            if (isMidLogin && result.token) {
                clearPending2faToken();
                localStorage.setItem("token", result.token);
                if (result.refreshToken) localStorage.setItem("refreshToken", result.refreshToken);
                window.location.href = "/dashboard";
                return;
            }
            if (result.success) {
                setStep("done");
                setMessage({ text: "Authenticator app activated successfully!", type: "success" });
            } else {
                setMessage({ text: result.error || "Invalid code.", type: "fail" });
            }
        } catch (error) {
            setMessage({ text: error.message || "Invalid code. Please try again.", type: "fail" });
            setCode("");
        }
        setIsLoading(false);
    };

    const getMessageClass = () => {
        if (message.type === "success") return "successmessage";
        return "failmessage";
    };

    // Loading state (post-login only, while fetching totp_enabled)
    if (totpEnabled === null) {
        return (
            <section className="login-page">
                <div className="container-fluid">
                    <div className="row align-items-center h-100">
                        <div className="col-md-6 col-sm-12 px-5 left-graphic">
                            <img src={codLogo} className="img-fluid" alt="CoD Logo" />
                        </div>
                        <div className="col-md-6 col-sm-12 px-5 form-outer h-100 align-items-center d-flex flex-column justify-content-center">
                            <div className="initial-form text-center py-4">
                                <div className="spinner-border text-primary" role="status" />
                                <p className="mt-2 text-muted">Loading...</p>
                            </div>
                            <div className="form-footer">
                                <p>Copyright &copy; {new Date().getFullYear()} Compliance on Demand. All Rights Are Reserved.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    // Already active (post-login)
    if (totpEnabled === true && step !== "done") {
        return (
            <section className="login-page">
                <div className="container-fluid">
                    <div className="row align-items-center h-100">
                        <div className="col-md-6 col-sm-12 px-5 left-graphic">
                            <img src={codLogo} className="img-fluid" alt="CoD Logo" />
                        </div>
                        <div className="col-md-6 col-sm-12 px-5 form-outer h-100 align-items-center d-flex flex-column justify-content-center">
                            <div className="initial-form">
                                <h3 className="h3 mb-3">Authenticator App</h3>
                                <div className="alert alert-success">
                                    Authenticator app is active. Contact your administrator to reset it if you've lost access.
                                </div>
                                <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                                    Go Back
                                </button>
                            </div>
                            <div className="form-footer">
                                <p>Copyright &copy; {new Date().getFullYear()} Compliance on Demand. All Rights Are Reserved.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="login-page">
            <div className="container-fluid">
                <div className="row align-items-center h-100">
                    <div className="col-md-6 col-sm-12 px-5 left-graphic">
                        <img src={codLogo} className="img-fluid" alt="CoD Logo" />
                    </div>
                    <div className="col-md-6 col-sm-12 px-5 form-outer h-100 align-items-center d-flex flex-column justify-content-center">
                        <div className="initial-form">
                            <h3 className="h3 mb-3">Authenticator App Setup</h3>

                            {message.text && (
                                <div className={`p-2 mb-20 ${getMessageClass()}`}>
                                    {message.text}
                                </div>
                            )}

                            {/* Step 1: Start */}
                            {step === "start" && (
                                <>
                                    <p className="text-muted mb-20">
                                        Use <strong>Microsoft Authenticator</strong> or any TOTP app to add an extra layer of security to your account.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-primary mb-20 w-100"
                                        onClick={handleSetup}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Generating..." : "Set Up Authenticator App"}
                                    </button>
                                </>
                            )}

                            {/* Step 2: Scan QR */}
                            {step === "scan" && (
                                <>
                                    <p className="mb-2"><strong>Step 1:</strong> Open <strong>Microsoft Authenticator</strong> → tap <strong>+</strong> → <strong>Other account</strong></p>
                                    <p className="mb-2"><strong>Step 2:</strong> Scan this QR code:</p>
                                    <img
                                        src={`data:image/png;base64,${qrCode}`}
                                        alt="TOTP QR Code"
                                        style={{ width: 200, height: 200, border: "1px solid #ddd", borderRadius: 4, display: "block", marginBottom: 12 }}
                                    />
                                    <p className="text-muted" style={{ fontSize: 13 }}>
                                        Can't scan? Enter this key manually: <code>{secret}</code>
                                    </p>
                                    <p className="mb-2"><strong>Step 3:</strong> Enter the 6-digit code from the app:</p>
                                    <div className="form-group mb-20">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="000000"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                            maxLength={6}
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary mb-20 w-100"
                                        onClick={handleConfirm}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Activating..." : "Activate"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0"
                                        style={{ fontSize: 13, color: "#888" }}
                                        onClick={() => setStep("start")}
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}

                            {/* Step 3: Done */}
                            {step === "done" && (
                                <p className="mb-20">
                                    <i className="bi bi-shield-check text-success" style={{ fontSize: 32 }}></i>
                                    <br />
                                    Your authenticator app is <strong>active</strong>. From now on you will be asked for a code from Microsoft Authenticator every time you log in.
                                    <br /><br />
                                    <span className="text-muted" style={{ fontSize: 13 }}>
                                        If you lose access to your authenticator app, contact your administrator to reset it.
                                    </span>
                                </p>
                            )}
                        </div>
                        <div className="form-footer">
                            <p>Copyright &copy; {new Date().getFullYear()} Compliance on Demand. All Rights Are Reserved.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TotpSetup;
